import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { abbrNum } from '@src/shared/includes/functions';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { isArray, isEmpty, isUndefined } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';

import flagIcon from './includes/flagIcon.svg';
import flagIconHover from './includes/flagIconHover.svg';

ChartJS.register(annotationPlugin, CategoryScale, Legend, LinearScale, LineElement, PointElement, Title, Tooltip);

const colors = {
  C360: '#2f4f4f',
  CATAMARAN: '#228b22',
  CIP: '#4b0082',
  CSP_FACETS: '#800000',
  FARO: '#ff8c00',
  GPS: '#00ff00',
  HSID: '#00ffff',
  ICUE: '#0000ff',
  IMDM: '#ff00ff',
  IRIS: '#efef00',
  RX_BOOK1: '#a0bff6',
  RX_BOOK2: '#ceab8b',
  TRANSUNION: '#ff0000',
};

/**
 * LineChart
 *
 * Displays the risk trend report data in a line chart.
 *
 * @param data
 * @param eventTypes
 * @param selectedEvents
 * @returns {React.JSX.Element|*|string}
 * @constructor
 */
export function LineChart({ data = {}, eventTypes = [], selectedEvents = [] }) {
  const chartRef = useRef(null);
  const iconRef = useRef(null);
  const iconHoverRef = useRef(null);

  const [labels, setLabels] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [annotations, setAnnotations] = useState({});
  const [events, setEvents] = useState([]);

  /**
   * Configure the chart labels and data sets from the api data.
   */
  useEffect(() => {
    const theEvents = [];

    if (isArray(selectedEvents) && !isEmpty(selectedEvents)) {
      selectedEvents.forEach((selectedEvent) => {
        const event = data?.events?.find((item) => {
          return item?.eventId === selectedEvent;
        });

        const theEventType = eventTypes?.find((eventType) => {
          return eventType?.codeId === event?.eventTypeCode;
        });

        const theEvent = {
          chartEndDate: dayjs(event?.displayEndDate).format('MM/DD/YYYY'),
          chartStartDate: dayjs(event?.displayStartDate).format('MM/DD/YYYY'),
          id: event?.eventId,
          mode: event?.displayStartDate === event?.displayEndDate ? 'single' : 'range',
          name: event?.title,
          tooltipEndDate: dayjs(event?.endDate).format('MM/DD/YYYY'),
          tooltipStartDate: dayjs(event?.startDate).format('MM/DD/YYYY'),
          type: theEventType?.codeDesc,
        };
        theEvents.push(theEvent);
      });
    }

    if (JSON.stringify(theEvents) !== JSON.stringify(events)) {
      setEvents(theEvents);
    }
  }, [selectedEvents]);

  /**
   * Load the flag icons.
   */
  useEffect(() => {
    iconRef.current = new Image(24, 24);
    iconRef.current.src = flagIcon?.src;

    iconHoverRef.current = new Image(24, 24);
    iconHoverRef.current.src = flagIconHover?.src;
  }, []);

  /**
   * getSingleIcon
   *
   * Get the single icon annotation.
   *
   * @param chart
   * @param event
   * @returns {{yScaleID: string, pointStyle: null, xValue: *, xScaleID: string, leave(*): void, yAdjust: number,
   *   enter(*): void, type: string, yValue: *}}
   */
  const getSingleIcon = (chart, event) => {
    return {
      enter(ctx) {
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].hovered = true;
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].pointStyle = iconHoverRef?.current;
        ctx.chart.update();
      },
      leave(ctx) {
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].hovered = false;
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].pointStyle = iconRef?.current;
        ctx.chart.update();
      },
      pointStyle: iconRef?.current,
      type: 'point',
      xScaleID: 'x',
      xValue: event?.chartStartDate,
      yAdjust: -16,
      yScaleID: 'y',
      yValue: chart?.scales?.y?.max,
    };
  };

  /**
   * getSingleLine
   *
   * Get the single line annotation.
   *
   * @param chart
   * @param event
   * @returns {{scaleID: string, borderDash: (function(*): *[]|number[]), borderColor: string, borderWidth: number,
   *   label: {textAlign: string, display: (function(*): boolean), yAdjust: number, z: number, position: string,
   *   content: *[]}, type: string, value: *}}
   */
  const getSingleLine = (chart, event) => {
    return {
      borderColor: 'rgba(255, 97, 43, 1.0)',
      borderDash: (ctx) => {
        return ctx?.chart?.config?.options?.plugins?.annotation?.annotations?.[`icon-${event?.id}`]?.hovered === true
          ? []
          : [3, 3];
      },
      borderWidth: 2,
      label: {
        content: [event?.name, event?.type, event?.tooltipStartDate],
        display: (ctx) => {
          return ctx?.chart?.config?.options?.plugins?.annotation?.annotations?.[`icon-${event?.id}`]?.hovered === true;
        },
        position: 'start',
        textAlign: 'left',
        yAdjust: 4,
        z: 100,
      },
      scaleID: 'x',
      type: 'line',
      value: event?.chartStartDate,
    };
  };

  /**
   * getRangeStartLine
   *
   * Get the range start line annotation.
   *
   * @param chart
   * @param event
   * @returns {{scaleID: string, borderDash: (function(*): *[]|number[]), borderColor: string, borderWidth: number,
   *   type: string, value: *}}
   */
  const getRangeStartLine = (chart, event) => {
    return {
      borderColor: 'rgba(255, 97, 43, 1.0)',
      borderDash: (ctx) => {
        return ctx?.chart?.config?.options?.plugins?.annotation?.annotations?.[`icon-${event?.id}`]?.hovered === true
          ? []
          : [3, 3];
      },
      borderWidth: 2,
      scaleID: 'x',
      type: 'line',
      value: event?.chartStartDate,
    };
  };

  /**
   * getRangeEndLine
   *
   * Get the range end line annotation.
   *
   * @param chart
   * @param event
   * @returns {{scaleID: string, borderDash: (function(*): *[]|number[]), borderColor: string, borderWidth: number,
   *   type: string, value: *}}
   */
  const getRangeEndLine = (chart, event) => {
    return {
      borderColor: 'rgba(255, 97, 43, 1.0)',
      borderDash: (ctx) => {
        return ctx?.chart?.config?.options?.plugins?.annotation?.annotations?.[`icon-${event?.id}`]?.hovered === true
          ? []
          : [3, 3];
      },
      borderWidth: 2,
      scaleID: 'x',
      type: 'line',
      value: event?.chartEndDate,
    };
  };

  /**
   * getRangeBox
   *
   * Get the range box annotation.
   *
   * @param chart
   * @param event
   * @returns {{backgroundColor: (function(*): string), borderColor: string, xMax: *, type: string, xMin: *}}
   */
  const getRangeBox = (chart, event) => {
    return {
      backgroundColor: (ctx) => {
        return ctx?.chart?.config?.options?.plugins?.annotation?.annotations?.[`icon-${event?.id}`]?.hovered === true
          ? 'rgba(255, 97, 43, 0.40)'
          : 'rgba(255, 97, 43, 0.10)';
      },
      borderColor: 'transparent',
      type: 'box',
      xMax: event?.chartEndDate,

      xMin: event?.chartStartDate,
    };
  };

  /**
   * getRangeHorizontalLine
   *
   * Get the range horizontal line annotation.
   *
   * @param chart
   * @param event
   * @returns {{yScaleID: string, yMin: *, borderColor: string, yMax: *, borderWidth: number, xScaleID: string,
   *   arrowHeads: {start: {borderColor: string, display: boolean}, end: {borderColor: string, display: boolean}},
   *   label: {textAlign: string, display: (function(*): boolean), yAdjust: number, z: number, content: (*|string)[]},
   *   xMax: *, type: string, xMin: *}}
   */
  const getRangeHorizontalLine = (chart, event) => {
    return {
      arrowHeads: {
        end: {
          borderColor: 'rgba(255, 97, 43, 1.0)',
          display: true,
        },
        start: {
          borderColor: 'rgba(255, 97, 43, 1.0)',
          display: true,
        },
      },
      borderColor: 'rgba(255, 97, 43, 1.0)',
      borderWidth: 2,
      label: {
        content: [event?.name, event?.type, `${event?.tooltipStartDate} - ${event?.tooltipEndDate}`],
        display: (ctx) => {
          return ctx?.chart?.config?.options?.plugins?.annotation?.annotations?.[`icon-${event?.id}`]?.hovered === true;
        },
        textAlign: 'left',
        yAdjust: 4,
        z: 100,
      },
      type: 'line',
      xMax: event?.chartEndDate,
      xMin: event?.chartStartDate,
      xScaleID: 'x',
      yMax: chart?.scales?.y?.max,
      yMin: chart?.scales?.y?.max,
      yScaleID: 'y',
    };
  };

  /**
   * getRangeIcon
   *
   * Get the range icon annotation.
   *
   * @param chart
   * @param event
   * @returns {{yScaleID: string, yMin: *, pointStyle: null, yMax: *, xScaleID: string, leave(*): void, yAdjust:
   *   number, xMax: *, enter(*): void, type: string, xMin: *}}
   */
  const getRangeIcon = (chart, event) => {
    return {
      enter(ctx) {
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].hovered = true;
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].pointStyle = iconHoverRef?.current;
        ctx.chart.update();
      },
      leave(ctx) {
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].hovered = false;
        ctx.chart.config.options.plugins.annotation.annotations[ctx?.id].pointStyle = iconRef?.current;
        ctx.chart.update();
      },
      pointStyle: iconRef?.current,
      type: 'point',
      xMax: event?.chartEndDate,
      xMin: event?.chartStartDate,
      xScaleID: 'x',
      yAdjust: -16,
      yMax: chart?.scales?.y?.max,
      yMin: chart?.scales?.y?.max,
      yScaleID: 'y',
    };
  };

  /**
   * Configure the chart annotations from the api data.
   */
  useEffect(() => {
    const chart = chartRef.current;

    if (!isEmpty(events)) {
      const theAnnotations = {};

      events.forEach((event) => {
        if (event?.mode === 'single') {
          theAnnotations[`icon-${event?.id}`] = getSingleIcon(chart, event);
          theAnnotations[`line-${event?.id}`] = getSingleLine(chart, event);
        }

        if (event?.mode === 'range') {
          theAnnotations[`lineStart-${event?.id}`] = getRangeStartLine(chart, event);
          theAnnotations[`lineEnd-${event?.id}`] = getRangeEndLine(chart, event);
          theAnnotations[`box-${event?.id}`] = getRangeBox(chart, event);
          theAnnotations[`horizontalLine-${event?.id}`] = getRangeHorizontalLine(chart, event);
          theAnnotations[`icon-${event?.id}`] = getRangeIcon(chart, event);
        }
      });

      if (theAnnotations !== annotations) {
        setAnnotations(theAnnotations);
      }
    } else {
      setAnnotations({});
    }
  }, [events]);

  /**
   * Configure the chart labels and data sets from the api data.
   */
  useEffect(() => {
    if (!isEmpty(data)) {
      const theLabels = [];
      const theDatasets = [];

      data?.series?.[0]?.dataPoints?.forEach((item) => {
        const theDate = dayjs(item?.date).format('MM/DD/YYYY');
        theLabels.push(theDate);
      });

      if (theLabels !== labels) {
        setLabels(theLabels);
      }

      data?.series?.forEach((item) => {
        const color = colors[item?.sourceCode];
        const dataset = {
          backgroundColor: color,
          borderColor: color,
          borderJoinStyle: 'bevel',
          borderWidth: 2,
          data: item?.dataPoints.map((theData) => {
            return theData?.value;
          }),
          label: item?.sourceCode,
          pointRadius: 3,
        };

        theDatasets.push(dataset);
      });

      if (theDatasets !== datasets) {
        setDatasets(theDatasets);
      }
    }
  }, [data]);

  return (
    <ErrorHandler location="src/pages/private/Dashboards/OperationsDashboard/components/LineChart/LineChart.jsx">
      <Line
        data={{
          datasets,
          labels,
        }}
        options={{
          animation: false,
          layout: {
            padding: {
              top: 36,
            },
          },
          plugins: {
            annotation: {
              annotations,
              clip: false,
              common: {
                drawTime: 'afterDraw',
              },
            },
            legend: {
              align: 'middle',
              position: 'right',
              title: {
                display: true,
                text: 'Sources',
              },
            },
            tooltip: {
              callbacks: {
                footer(context) {
                  const record = data?.series?.find((item) => {
                    return item?.sourceCode === context?.[0]?.dataset?.label;
                  });

                  if (isUndefined(record)) {
                    return '';
                  }

                  const item = record?.dataPoints?.[context?.[0]?.dataIndex];

                  if (isUndefined(item)) {
                    return '';
                  }

                  return `\nValue: ${Number(item?.value).toLocaleString('en-US')}\nTrend Value: ${Number(
                    item?.trendValue
                  ).toLocaleString('en-US')}\nPercent Change: ${item?.percChange}%\n`;
                },
                label(context) {
                  return `${context?.dataset?.label} (${context?.label})`;
                },
                title() {
                  return 'Untrusted Source';
                },
              },
            },
          },
          responsive: true,
          scales: {
            x: {
              border: { color: themeConfiguration?.theme?.colors?.gray6, dash: [5, 5] },
              grid: {
                color: themeConfiguration?.theme?.colors?.gray3,
              },
              title: {
                display: true,
                text: 'Date Range',
              },
            },
            y: {
              border: { color: themeConfiguration?.theme?.colors?.gray6, dash: [5, 5] },
              grid: {
                color: themeConfiguration?.theme?.colors?.gray3,
              },
              stacked: false,
              ticks: {
                // Include a dollar sign in the ticks
                callback(value) {
                  return abbrNum(value);
                },
              },
              title: {
                display: true,
                text: 'Number of Entities',
              },
            },
          },
        }}
        ref={chartRef}
      />
    </ErrorHandler>
  );
}
